import { useEffect, useRef, useState, type DragEvent } from 'react';
import { CloseIcon, UploadIcon } from '@bravemove/design-tokens/icons';
import { cn } from '../../utils/cn';

export interface UploadedImage {
  readonly file: File;
  readonly previewUrl: string;
}

export interface ImageUploaderProps {
  readonly value: readonly UploadedImage[];
  readonly onChange: (images: readonly UploadedImage[]) => void;
  readonly maxImages?: number;
  readonly maxBytes?: number;
  readonly accept?: string;
  readonly hint?: string;
  readonly disabled?: boolean;
  readonly invalid?: boolean;
  readonly className?: string;
}

const DEFAULT_ACCEPT = 'image/png,image/jpeg,image/webp,image/avif,image/heic';

export function ImageUploader({
  value,
  onChange,
  maxImages = 10,
  maxBytes = 5 * 1024 * 1024,
  accept = DEFAULT_ACCEPT,
  hint = `Up to ${maxImages} images, ${Math.round((maxBytes / 1024 / 1024) * 10) / 10} MB each`,
  disabled,
  invalid,
  className,
}: ImageUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  // Revoke object URLs when items leave the list.
  useEffect(() => {
    return () => {
      value.forEach((img) => URL.revokeObjectURL(img.previewUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addFiles = (files: FileList | readonly File[]) => {
    const arr = Array.from(files).filter((f) => f.type.startsWith('image/') && f.size <= maxBytes);
    const remaining = maxImages - value.length;
    const next = [
      ...value,
      ...arr.slice(0, Math.max(0, remaining)).map((file) => ({
        file,
        previewUrl: URL.createObjectURL(file),
      })),
    ];
    onChange(next);
  };

  const remove = (index: number) => {
    const removed = value[index];
    if (removed) URL.revokeObjectURL(removed.previewUrl);
    const next = value.slice();
    next.splice(index, 1);
    onChange(next);
  };

  const reorder = (from: number, to: number) => {
    if (from === to) return;
    const next = value.slice();
    const [moved] = next.splice(from, 1);
    if (!moved) return;
    next.splice(to, 0, moved);
    onChange(next);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
  };

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        aria-invalid={invalid || undefined}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          if (disabled) return;
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={cn(
          'flex flex-col items-center justify-center gap-2 p-6 rounded-xl border-2 border-dashed',
          'transition-colors duration-[120ms] cursor-pointer text-center',
          'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/25',
          dragOver
            ? 'border-accent bg-accent-soft text-accent'
            : 'border-hairline-2 bg-surface text-ink-2 hover:border-accent/60',
          invalid && 'border-danger',
          disabled && 'opacity-45 cursor-not-allowed',
        )}
      >
        <UploadIcon size={28} color="currentColor" />
        <div className="text-[13.5px] font-semibold text-ink">
          Drop images here, or click to browse
        </div>
        <div className="text-[11.5px] text-ink-3">{hint}</div>
        <input
          ref={inputRef}
          type="file"
          className="sr-only"
          accept={accept}
          multiple
          disabled={disabled}
          onChange={(e) => e.target.files && addFiles(e.target.files)}
        />
      </div>

      {value.length > 0 ? (
        <ul className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
          {value.map((img, i) => (
            <li
              key={img.previewUrl}
              draggable={!disabled}
              onDragStart={(e) => {
                e.dataTransfer.setData('text/plain', String(i));
                e.dataTransfer.effectAllowed = 'move';
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
              }}
              onDrop={(e) => {
                e.preventDefault();
                const from = Number(e.dataTransfer.getData('text/plain'));
                if (Number.isFinite(from)) reorder(from, i);
              }}
              className="relative aspect-square rounded-md overflow-hidden border border-hairline bg-surface-2 group"
            >
              <img src={img.previewUrl} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => remove(i)}
                aria-label="Remove image"
                className="absolute top-1 end-1 size-6 inline-flex items-center justify-center rounded-full bg-surface/90 text-ink-2 hover:bg-danger hover:text-white focus-visible:outline-none focus-visible:ring-[2px] focus-visible:ring-accent/40 transition-colors"
              >
                <CloseIcon size={12} color="currentColor" />
              </button>
              {i === 0 ? (
                <span className="absolute bottom-1 start-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-accent text-accent-ink font-mono">
                  Cover
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
